import { getTranscriptions } from '../api/AudioAPI';
import { createBase64FromBlob } from '../api/createBase64';

import { hasAudioContent, hasAudioContentComprehensive } from './AudioDetection';
import { createPCM16FromAudioBuffer } from './createPCM16FromAudioBuffer';
import { createWAVFromAudioBuffer } from './createWAVFromAudioBuffer';
import { getLastRecorderAudioChunk } from './getLastRecorderAudioChunk';

/**
 * Processes audio chunks to text transcription
 * This function is designed to work with mono audio (single channel) as configured in getUserMedia
 * Includes sound detection to skip processing silent audio chunks
 */
export const processWEBMAudioToTextWithWAV = async (
	audioChunks: Blob[],
	lastProcessedPosition: number,
	sampleRate: number,
) => {
	const completeAudio = new Blob(audioChunks);

	const { audioBuffer, newAudioEndPosition } = await getLastRecorderAudioChunk(
		completeAudio,
		lastProcessedPosition,
		sampleRate,
	);

	if (!audioBuffer) {
		return { audioString: null, newAudioEndPosition };
	}

	const wavBuffer = createWAVFromAudioBuffer(audioBuffer);
	const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

	const audioString = await createBase64FromBlob(wavBlob);
	const transcription = await getTranscriptions({ audioString });

	return { transcription: transcription[0], newAudioEndPosition };
};

/**
 * Processes audio chunks to base64 string (V2)
 * This function is designed to work with mono audio (single channel) as configured in getUserMedia
 * Includes sound detection to skip processing silent audio chunks
 */
export const processWEBMAudioToTextWithPCM16 = async (
	audioChunks: Blob[],
	lastProcessedPosition: number,
	sampleRate: number,
	soundDetection: {
		enableSoundDetection: boolean;
		soundThreshold: number;
		useComprehensiveDetection: boolean;
	} = {
		enableSoundDetection: true,
		useComprehensiveDetection: false,
		soundThreshold: 0.005,
	},
) => {
	const { enableSoundDetection, useComprehensiveDetection, soundThreshold } = soundDetection;
	const completeAudio = new Blob(audioChunks);

	const { audioBuffer, newAudioEndPosition } = await getLastRecorderAudioChunk(
		completeAudio,
		lastProcessedPosition,
		sampleRate,
	);

	if (!audioBuffer) {
		return { audioString: null, newAudioEndPosition };
	}

	if (enableSoundDetection) {
		const hasSound = useComprehensiveDetection
			? hasAudioContentComprehensive(audioBuffer, { rmsThreshold: soundThreshold })
			: hasAudioContent(audioBuffer, soundThreshold);

		if (!hasSound) {
			return { audioString: null, newAudioEndPosition };
		}
	}

	const pcm16Buffer = createPCM16FromAudioBuffer(audioBuffer);
	const pcm16Blob = new Blob([pcm16Buffer], { type: 'audio/pcm' });

	const audioString = await createBase64FromBlob(pcm16Blob);

	return { audioString, newAudioEndPosition };
};
